import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { getApp } from '@angular/fire/app';
import {
  applyActionCode,
  Auth,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  signInAnonymously,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updatePassword,
  verifyPasswordResetCode,
} from '@angular/fire/auth';
import { doc, Firestore, getDoc, setDoc, updateDoc } from '@angular/fire/firestore';
import { deleteObject, getStorage, ref, Storage, uploadBytesResumable } from '@angular/fire/storage';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { BehaviorSubject, catchError, firstValueFrom, from, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { EnvService } from './env.service';
import { TranscriptionModel } from './shared/components/recorders/audio-recorder/audio-recorder.dto';
import { AppConfirmService } from './shared/services/app-confirm/app-confirm.service';
import { TenantService } from './shared/services/tenant/tenant.service';
import { ToastService } from './shared/services/toast/toast.service';

interface userData {
  _id: any;
  initials: any;
  fullName: any;
  email: any;
  role: any;
  photo: any;
  type: any;
  isOwner: boolean;
  canUseAI: boolean;
  phone: any;
  company: any;
  truncatedFullName?: any;
  isAnonymous?: boolean;
}

export interface PendingInvitation {
  _id: string;
  email: string;
  role: string;
  inviterName: string;
  workspaceName: string;
  tenantId: string;
  expireAt: string;
  createdAt: string;
}

export interface JoinLinkItem {
  _id: string;
  tenantId: string;
  token: string;
  name: string;
  groupId?: string | null;
  allowedDomain?: string | null;
  role?: string;
  enabled: boolean;
  joinUrl: string;
  createdAt?: string;
}

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

export interface AudioVoiceSettings {
  voiceType: string;
  speed: number;
  pitch: number;
}

export interface PaginatorSettings {
  [key: string]: number;
}

export interface SearchSettings {
  textToSearch: string;
  compoundOptions: string;
  searchType: string;
  minScore: string;
  includeOutOfContext: boolean;
}

export interface Project {
  _id: string;
  name: string;
  createdAt: string;
}

export interface UserConfiguration {
  language: 'fr' | 'en' | string;
  transcriptionAI: string;
  TTSAIModel: {
    en: string;
    fr: string;
  };
  audioConfiguration: {
    en: AudioVoiceSettings;
    fr: AudioVoiceSettings;
  };
  menuItems: string[];
  searchAI: string;
  chunkCreator: {
    generateTranslation: boolean;
    generateVoice: boolean;
    generateAiAfterTranscription: boolean;
    generateEmbeddings: boolean;
  };
  transcriptionSettings: {
    model: TranscriptionModel;
    prompt?: string;
    template?: string | null;
  };
  generativeAISettings: {
    template: string | null;
  };
  paginator: PaginatorSettings;
  popups: {
    microphoneAccess: boolean;
    cameraAccess: boolean;
  };
  searchSettings: SearchSettings;
  defaultProject?: Project;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly env = inject(EnvService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly confirmService = inject(AppConfirmService);
  private readonly tenantService = inject(TenantService);
  private readonly auth = inject(Auth);
  private readonly storage = inject(Storage);
  private readonly firestore = inject(Firestore);

  private userInfoSource = new BehaviorSubject<any>({});
  private refreshConfig$ = new BehaviorSubject<void>(undefined);
  readonly isEmailVerified = signal<boolean>(false);

  firebaseGoogleProvider = new GoogleAuthProvider();
  firebaseMicrosoftProvider = new OAuthProvider('microsoft.com');

  userInfo$ = this.userInfoSource.asObservable();
  private readonly userInfoSignal = toSignal(this.userInfo$, { initialValue: {} });
  readonly userAppRole = computed(() => this.userInfoSignal()?.type ?? 'Reader');

  readonly currentUser = computed<userData>(() => {
    const user = this.userInfoSignal();
    const nameLengthLimit = 8;

    if (user && Object.keys(user).length > 0) {
      const firstName = user.firstName || user.fullName?.split(' ')[0] || '';
      const lastName = user.lastName || user.fullName?.split(' ').slice(1).join(' ') || '';
      const fullName = firstName + ' ' + lastName;
      const firstNameTooLong = firstName.length > nameLengthLimit;
      const lastNameTooLong = lastName.length > nameLengthLimit;
      const truncatedFirstName = firstNameTooLong ? firstName.substring(0, nameLengthLimit) : firstName;
      const truncatedLastName = lastNameTooLong ? lastName.substring(lastName.length - nameLengthLimit) : lastName;
      const truncatedFullName = firstNameTooLong || lastNameTooLong ? `${truncatedFirstName} ... ${truncatedLastName}` : fullName;

      // Resolve role/type from tenants array (new format) or fallback to root-level type (legacy)
      let resolvedType = user.type;
      let resolvedIsOwner = user.isOwner || false;

      if (user.tenants && Array.isArray(user.tenants) && user.tenants.length > 0) {
        const activeTenantId = this.tenantService.activeTenantId() || user.tenantId;
        const activeTenant = user.tenants.find((t: any) => t.tenantId === activeTenantId) || user.tenants[0];
        if (activeTenant) {
          // Map new role format ('admin' -> 'Admin', 'user' -> 'Reader') to legacy type values
          const roleMap: Record<string, string> = { admin: 'Admin', writer: 'Writer', reader: 'Reader', user: 'Reader' };
          resolvedType = roleMap[activeTenant.role?.toLowerCase()] || activeTenant.role || resolvedType;
          resolvedIsOwner = activeTenant.isOwner || false;
        }
      }

      return {
        _id: user.id || user._id,
        initials: user.initials || 'G',
        fullName: user.fullName || fullName,
        truncatedFullName: truncatedFullName || user.fullName || fullName,
        type: resolvedType || 'Reader',
        isOwner: resolvedIsOwner,
        canUseAI: user.canUseAI !== undefined ? !!user.canUseAI : !user.isAnonymous,
        email: user.email || 'anonymous@guest.com',
        role: user.role || 'guest',
        photo: user.photo,
        phone: user.phone,
        company: user.company,
        isAnonymous: user.isAnonymous || false,
      };
    } else {
      return {
        _id: null,
        initials: 'U',
        fullName: '',
        email: 'no@user.found',
        role: 'no role',
        type: 'no type',
        isOwner: false,
        canUseAI: false,
        photo: undefined,
        phone: undefined,
        company: undefined,
        truncatedFullName: '',
      };
    }
  });

  BASE_URL_NO_AUTH;
  BASE_URL;
  client;
  currentToken;
  authReady = false;
  userSessionSubscription;
  sessionSubscriptionInterval;
  defaultSliderValues = {
    speed: 1,
    pitch: 0,
  };

  userConfiguration$: Observable<UserConfiguration>;

  constructor() {
    this.BASE_URL = `${this.env.apiUrl}auth`;
    this.BASE_URL_NO_AUTH = `${this.env.apiUrl}`;
    const cachedUser = localStorage.getItem('userData');
    if (cachedUser) {
      this.userInfoSource.next(JSON.parse(cachedUser));
    }
    this.firebaseMicrosoftProvider.setCustomParameters({});
  }

  async sendVerificationEmail(): Promise<void> {
    if (this.auth.currentUser) {
      const email = this.auth.currentUser.email;
      await firstValueFrom(this.http.post(`${this.BASE_URL}/send-verification-email`, { email }, httpOptions));
    }
  }

  async checkEmailVerificationStatus(): Promise<boolean> {
    if (this.auth.currentUser) {
      await this.auth.currentUser.reload();
      const verified = this.auth.currentUser.emailVerified;
      this.isEmailVerified.set(verified);
      if (verified) {
        try {
          const tok = await this.auth.currentUser.getIdToken(true);
          const loginData = { token: tok };
          localStorage.setItem('loginData', JSON.stringify(loginData));
        } catch (tokenErr) {
          console.error('Failed to force refresh ID token after checking verification status:', tokenErr);
        }
      }
      return verified;
    }
    return false;
  }

  async verifyEmailToken(token: string): Promise<void> {
    await applyActionCode(this.auth, token);
    if (this.auth.currentUser) {
      await this.auth.currentUser.reload();
      const verified = this.auth.currentUser.emailVerified;
      this.isEmailVerified.set(verified);
      if (verified) {
        try {
          const tok = await this.auth.currentUser.getIdToken(true);
          const loginData = { token: tok };
          localStorage.setItem('loginData', JSON.stringify(loginData));
        } catch (tokenErr) {
          console.error('Failed to force refresh ID token after verifyEmailToken:', tokenErr);
        }
      }
    }
  }

  init(): () => Promise<void> {
    return () =>
      new Promise<void>((resolve) => {
        this.getUserConfiguration();
        this.translate.onLangChange.subscribe(() => {
          try {
            this.auth.languageCode = this.translate.currentLang || localStorage.getItem('language') || 'fr';
          } catch (E) {
            console.error(E);
          }
        });

        onAuthStateChanged(this.auth, (user) => {
          if (user) {
            this.isEmailVerified.set(user.emailVerified);
            user.getIdToken(true).then((tok) => {
              const loginData = { token: tok };
              if (user.isAnonymous) {
                sessionStorage.setItem('loginData', JSON.stringify(loginData));
                localStorage.removeItem('loginData'); // Ensure no stale localStorage exists for anonymous users
                
                const guestProfile = {
                  id: user.uid,
                  fullName: 'Guest',
                  type: 'Reader',
                  isAnonymous: true
                };
                this.userInfoSource.next(guestProfile);
                this.authReady = true;
                resolve();
              } else {
                localStorage.setItem('loginData', JSON.stringify(loginData));
                this.getUserData({ id: user.uid })
                  .then((res) => {
                    // Initialize tenant context from user data, but skip if on join flow to prevent premature reload
                    if (res?.tenants && !this.router.url.startsWith('/join/')) {
                      this.tenantService.initializeTenant(res);
                    }
                    this.setAuthCookie();
                    this.tenantService.loadTenantAiSettings();
                    if (localStorage.getItem('sessionUUID')) {
                      this.startSessionSubscription(user.uid);
                    } else {
                      this.saveUserSession(user.uid).then(() => this.startSessionSubscription(user.uid));
                    }
                    if (this.router.url === '/login') {
                      this.router.navigateByUrl('/knowledge/list');
                    }
                    this.authReady = true;
                    resolve();
                  })
                  .catch((err) => {
                    console.error("Couldn't get user data", err);
                    this.logOut();
                    resolve(); // Resolve anyway so app doesn't hang
                  });
              }
            });
          } else {
            // No user is signed in.
            this.isEmailVerified.set(true);
            this.clearLocalSession();
            resolve();
          }
        });
      });
  }

  getSseTicket(transactionId: string): Observable<{ ticket: string }> {
    const body = { transactionId };
    return this.http.post<{ ticket: string }>(`${this.BASE_URL}/sse-ticket`, body, httpOptions);
  }

  async getValidToken(): Promise<{ token: string; decodedToken: any } | null> {
    if (!this.auth.currentUser) {
      localStorage.removeItem('loginData');
      localStorage.removeItem('loginDate');
      localStorage.removeItem('userData');
      localStorage.removeItem('userGroups');
      localStorage.removeItem('apiToken');
      sessionStorage.removeItem('loginData');
      sessionStorage.removeItem('loginDate');
      return null;
    }

    let token = this.getTokenFromStorage();

    // No token, nothing to do.
    if (!token) {
      return null;
    }

    let decodedToken = this.decodeToken(token);

    // If token is malformed or has no expiration, force a refresh.
    if (!decodedToken || !decodedToken.exp) {
      await this.getNewToken();
      token = this.getTokenFromStorage();
      decodedToken = this.decodeToken(token);
      return { token, decodedToken };
    }

    // Check if the token is expired or will expire in the next minute.
    const isExpired = decodedToken.exp * 1000 < Date.now() - 60000;

    if (isExpired) {
      await this.getNewToken();
      token = this.getTokenFromStorage();
      decodedToken = this.decodeToken(token);
      return { token, decodedToken };
    }

    return { token, decodedToken };
  }

  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Error decoding token:', e);
      return null;
    }
  }

  private getTokenFromStorage(): string | null {
    if (this.auth.currentUser?.isAnonymous) {
      const sessionData = JSON.parse(sessionStorage.getItem('loginData')) || {};
      return sessionData.token || null;
    }
    const localData = JSON.parse(localStorage.getItem('loginData')) || {};
    if (localData.token) return localData.token;

    const sessionData = JSON.parse(sessionStorage.getItem('loginData')) || {};
    return sessionData.token || null;
  }

  isAdmin() {
    const type = this.userInfo()?.type;
    return type === 'Admin' || type === 'admin';
  }

  setCurrentUser(userData) {
    // Normalize type from tenants array if not set at root level (new user format)
    if (userData && !userData.type && userData.tenants?.length > 0) {
      const activeTenantId = localStorage.getItem('active_workspace') || userData.tenantId;
      const activeTenant = userData.tenants.find((t: any) => t.tenantId === activeTenantId) || userData.tenants[0];
      if (activeTenant?.role) {
        const roleMap: Record<string, string> = { admin: 'Admin', writer: 'Writer', reader: 'Reader', user: 'Reader' };
        userData.type = roleMap[activeTenant.role.toLowerCase()] || activeTenant.role;
        userData.isOwner = activeTenant.isOwner || false;
      }
    }
    this.userInfoSource.next(userData);
    if (userData && !userData.isAnonymous && !this.auth.currentUser?.isAnonymous) {
      localStorage.setItem('userData', JSON.stringify(userData));
    }
  }

  reauthWithCredential(password) {
    return new Promise(async (resolve, reject) => {
      const userData: any = await this.getUserData();
      const email = userData.email;
      const creds = EmailAuthProvider.credential(email, password);
      reauthenticateWithCredential(this.auth.currentUser, creds)
        .then(() => {
          resolve({});
        })
        .catch(() => {
          reject();
        });
    });
  }
  isWriter() {
    return this.userInfo() && (this.userInfo().type === 'Writer' || this.userInfo().type === 'Admin' || this.userInfo().role === 'dev');
  }

  deleteFile(filename: string) {
    const storage = this.storage;
    const activeTenant = this.tenantService.activeTenantId() || this.env.appName;
    const storagePath = `${activeTenant}/${filename}`;
    const fileRef = ref(storage, storagePath);

    return deleteObject(fileRef);
  }

  uploadFile(file: File, filename: string, bucket: string = undefined, includeAppNameInPath: boolean = true) {
    return new Promise(async (resolve, reject) => {
      try {
        this.getValidToken();

        let storage;
        if (bucket) {
          const app = getApp();
          storage = getStorage(app, bucket);
        } else {
          storage = this.storage;
        }

        const activeTenant = this.tenantService.activeTenantId() || this.env.appName;
        const path = includeAppNameInPath ? `${activeTenant}/${filename}` : filename;

        const storageRef = ref(storage, path);
        const snapshot = await uploadBytesResumable(storageRef, file);
        resolve(snapshot);
      } catch (err) {
        this.toast.snackbar.open(this.translate.instant('Snackbar.FileUploadFail'), 'Ok', { duration: 3000 });
        resolve(err); // If you want to keep the promise rejection behavior
      }
    });
  }

  checkAccount(email) {
    return this.http.post(`${this.BASE_URL}/users/checkAccount`, { email });
  }

  /**
   * This function is designed to disconnect the user when he is connected with the same account
   * multiple times in the same application. The latest connected account stays connected
   * and the other ones are disconnected.
   * @dependency saveUserSession
   */
  startSessionSubscription(userID) {
    // Disabled for now
    // interval is used instead of onSnapshot to reduce the network calls
    // this.sessionSubscriptionInterval = setInterval(() => {
    //   const userDocRef = doc(this.firestore, 'user_tokens', userID);
    //   getDoc(userDocRef)
    //     .then((docSnapshot) => {
    //       if (docSnapshot.exists()) {
    //         const res: any = docSnapshot.data();
    //         if (res.sessions) {
    //           if (!res.sessions[this.env.appName]) {
    //             this.saveUserSession(userID);
    //           } else {
    //             if (res.sessions[this.env.appName] !== localStorage.getItem('sessionUUID')) {
    //               this.confirmService.confirmSessionExpulsion().subscribe((confirmRes) => {
    //                 if (confirmRes) {
    //                   this.saveUserSession(userID);
    //                 } else {
    //                   this.logOut();
    //                 }
    //               });
    //             }
    //           }
    //         }
    //       }
    //     })
    //     .catch((error) => {
    //       console.error('Error getting user document:', error);
    //     });
    // }, 60000);
  }
  /**
   * This function saves the user session by appName. This way the same user can be connected
   * with the same account in different applications.
   *  @dependency startSessionSubscription
   */
  saveUserSession(userID) {
    const uuid = uuidv4();
    localStorage.setItem('sessionUUID', uuid);

    const userDocRef = doc(this.firestore, 'user_tokens', userID);

    return getDoc(userDocRef).then((docSnapshot) => {
      if (docSnapshot.exists()) {
        return updateDoc(userDocRef, {
          _id: userID,
          session: uuid,
          [`sessions.${this.env.appName}`]: uuid,
        });
      } else {
        return setDoc(userDocRef, {
          _id: userID,
          session: uuid,
          [`sessions.${this.env.appName}`]: uuid,
        });
      }
    });
  }



  canUseAI(): boolean {
    const user = this.currentUser();
    if (!user?.canUseAI) return false;
    if (this.isAdmin()) return true;

    const aiSettings = this.tenantService.tenantAiSettings();
    if (aiSettings.aiAccessMode === 'all') return true;
    if (aiSettings.aiAccessMode === 'restricted') {
      const userId = user?._id;
      if (!userId || !aiSettings.aiAllowedUsers) return false;
      return aiSettings.aiAllowedUsers.includes(userId);
    }
    return true;
  }

  tokenRefreshInterval;
  async updateUserPassword(credentials) {
    const auth = this.auth;
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error('No authenticated user found');
    }

    const creds = EmailAuthProvider.credential(credentials.email, credentials.password);

    await reauthenticateWithCredential(currentUser, creds);
    await updatePassword(currentUser, credentials.newPassword);

    const result = await this.updatePassword(credentials);

    return result;
  }

  private activeGetInfoRequest$: Observable<any> | null = null;

  async getUserData(userData = null, rejectOnNetworkError = false): Promise<any> {
    if (!userData) {
      userData = { _id: null };
    }
    // The 'rgetInfo' cache-busting and 'initialReq' token generation logic have been removed.
    return new Promise(async (resolve, reject) => {
      this.getValidToken()
        .then(() => {
          if (!this.activeGetInfoRequest$) {
            const url = `${this.BASE_URL}/users/getInfo/?userid=${userData.id || this.userInfo()._id}`;
            this.activeGetInfoRequest$ = this.http.get(url).pipe(shareReplay(1));
          }

          this.activeGetInfoRequest$.subscribe({
            next: (data: any) => {
              this.activeGetInfoRequest$ = null; // Clear so subsequent calls fetch fresh data
              if (data) {
                if (data && !data.isAnonymous && !this.auth.currentUser?.isAnonymous) {
                  localStorage.setItem('userData', JSON.stringify(data));
                }
                this.setCurrentUser(data);
                resolve(data);
              } else {
                reject();
              }
            },
            error: (err) => {
              this.activeGetInfoRequest$ = null;
              if (rejectOnNetworkError) {
                reject(err);
                this.toast.snackbar.open(this.translate.instant('Snackbar.NoAccessToThisApp'), 'Ok', { duration: 2000 });
              }
              if (!(err instanceof HttpErrorResponse)) {
                reject(err);
              }
            },
          });
        })
        .catch((err) => {
          console.error(err);
          reject(err);
        });
    });
  }

  getBaseURL() {
    return this.BASE_URL;
  }

  login(credentials, skipGetUserData = false): Promise<any> {
    return new Promise((resolve, reject) => {
      signInWithEmailAndPassword(this.auth, credentials.email, credentials.password)
        .then(async (data) => {
          try {
            const token = await data.user.getIdToken(true);
            localStorage.setItem('loginData', JSON.stringify({ token }));
            localStorage.setItem('loginDate', JSON.stringify(moment().format()));
          } catch (e) {
            console.error('Failed to get user ID token immediately', e);
          }
          await this.getValidToken();
          
          if (skipGetUserData) {
            resolve(data.user);
            return;
          }

          this.getUserData({ id: data.user.uid, credentials })
            .then(async (res) => {
              if (!localStorage.getItem('sessionUUID')) {
                await this.saveUserSession(data.user.uid);
                this.startSessionSubscription(data.user.uid);
              }

              resolve(res);
            })
            .catch((error) => {
              this.logOut();
              reject(error);
            });
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  loginAnonymously(): Promise<any> {
    return new Promise((resolve, reject) => {
      signInAnonymously(this.auth)
        .then(async (result) => {
          try {
            const token = await result.user.getIdToken();
            const loginData = { token };
            sessionStorage.setItem('loginData', JSON.stringify(loginData));
            resolve(result);
          } catch (err) {
            reject(err);
          }
        })
        .catch((error) => reject(error));
    });
  }

  loginWithProvider(provider, skipGetUserData = false) {
    return new Promise((resolve, reject) => {
      let chosenProvider;
      switch (provider) {
        case 'google':
          chosenProvider = this.firebaseGoogleProvider;
          break;
        case 'microsoft':
          chosenProvider = this.firebaseMicrosoftProvider;
          break;
        default:
          reject();
          return;
      }
      signInWithPopup(this.auth, chosenProvider)
        .then(async (result) => {
          const loginData = {
            token: await result.user.getIdToken(),
          };
          localStorage.setItem('loginData', JSON.stringify(loginData));

          const user = result.user;
          
          if (skipGetUserData) {
            resolve(user);
            return;
          }

          this.getUserData({ id: user.uid }, true)
            .then(async (res) => {
              if (!localStorage.getItem('sessionUUID')) {
                await this.saveUserSession(user.uid);
                this.startSessionSubscription(user.uid);
              }
              resolve(res);
            })
            .catch((err) => {
              // Ignore firebase errors
              if (err.name !== 'FirebaseError') {
                reject(err);
                this.toast.snackbar.open(this.translate.instant('Snackbar.NoAccessToThisApp'), 'Ok', { duration: 2000 });
                this.logOut();
              } else {
                this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
                this.logOut();
              }
            });
        })
        .catch((error) => {
          this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', {
            duration: 2000,
          });
          console.error(error);
          reject(error);
        });
    });
  }

  private async getNewToken() {
    try {
      const isSession = this.auth.currentUser?.isAnonymous || !!sessionStorage.getItem('loginData');
      const storage = isSession ? sessionStorage : localStorage;

      const data = JSON.parse(storage.getItem('loginData')) || {};
      data.token = await this.auth.currentUser.getIdToken(true);
      storage.setItem('loginData', JSON.stringify(data));
      storage.setItem('loginDate', JSON.stringify(moment().format()));
      return true;
    } catch (err) {
      return false;
    }
  }

  generateToken(credentials) {
    return this.http.post(`${this.BASE_URL}/users/genToken`, credentials);
  }
  deleteCurrentToken() {
    return this.http.post(`${this.BASE_URL}/users/deleteCurrentToken`, httpOptions);
  }
  removeFPassToken(token) {
    return this.http.get(`${this.BASE_URL}/rmToken/forgotPass/?token=${token}`);
  }
  removeConfirmToken(token) {
    return this.http.get(`${this.BASE_URL}/rmToken/confirmMail/?token=${token}`);
  }
  checkPassToken(token): Observable<any> {
    return this.http.get(`${this.BASE_URL}/checkToken/forgotPass/?token=${token}`);
  }
  checkConfEmailToken(token): Observable<any> {
    return this.http.get(`${this.BASE_URL}/checkToken/mailConfirmation/?token=${token}`);
  }
  checkEmail(email): Observable<any> {
    return this.http.get(`${this.BASE_URL}/userExist/?token=${email}`);
  }
  registerConfirmation(token): Observable<any> {
    return this.http.get(`${this.BASE_URL}/registerConf/?token=${token}`);
  }
  resendConfirmationEmail(email) {
    return this.client.auth.resendConfirmationEmail(email);
  }

  testHeaders(credentials): Observable<any> {
    const headers = new HttpHeaders();
    return this.http.post(`${this.BASE_URL}/users/test`, credentials, httpOptions);
  }

  sendPasswordRecoveryEmail(destination): Observable<any> {
    return this.http.post(`${this.BASE_URL}/users/forgotPassword`, { email: destination }, httpOptions);
  }

  changePass(email, newPass): Observable<any> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this.http.post(`${this.BASE_URL}/setPassword`, { em: email, npass: newPass }, options);
  }

  userIsLoggedIn(): boolean {
    return localStorage.getItem('loginData') != null && localStorage.getItem('apiToken') != null;
  }

  static lastRefreshDate = null;

  getCurrentUser() {
    if (this.client) {
      return this.client.auth.user;
    } else {
      return JSON.parse(localStorage.getItem('userData'));
    }
  }

  getID() {
    return this.userInfo()._id;
  }

  userInfo(): userData {
    return this.currentUser();
  }

  clearLocalSession() {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('loginData');
    localStorage.removeItem('loginDate');
    localStorage.removeItem('create-mode');
    localStorage.removeItem('sessionUUID');
    localStorage.removeItem('apiToken');
    localStorage.removeItem('configurations');
    localStorage.removeItem('active_workspace');
    sessionStorage.removeItem('loginData');
    sessionStorage.removeItem('loginDate');
    if (this.sessionSubscriptionInterval) {
      clearInterval(this.sessionSubscriptionInterval);
    }
    this.clearAuthCookie();
  }

  logOut() {
    this.clearLocalSession();
    signOut(this.auth)
      .then(() => {
        setTimeout(() => {
          this.router.navigateByUrl('/login');
        }, 2000);
      })
      .catch((e) => {});
  }

  setAuthCookie() {
    const hostname = window.location.hostname;
    let domainString = '';
    // Only set the domain if we are on the main skillztank.com domain
    if (hostname.includes('skillztank.com')) {
      domainString = 'domain=.skillztank.com; ';
    }
    const cookieOptions = `${domainString}path=/; max-age=2592000; SameSite=Lax`;
    document.cookie = `skz_is_logged_in=true; ${cookieOptions}`;

    // Also store user email if available
    const userEmail = this.userInfoSource.value?.email;
    if (userEmail) {
      document.cookie = `skz_user_email=${encodeURIComponent(userEmail)}; ${cookieOptions}`;
    }
  }

  clearAuthCookie() {
    const hostname = window.location.hostname;
    let domainString = '';
    if (hostname.includes('skillztank.com')) {
      domainString = 'domain=.skillztank.com; ';
    }
    const cookieOptions = `${domainString}path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `skz_is_logged_in=; ${cookieOptions}`;
    document.cookie = `skz_user_email=; ${cookieOptions}`;
  }

  signup(credentials): Observable<any> {
    return this.http.post(`${this.BASE_URL}/users`, credentials, httpOptions);
  }

  /**
   * Creates a new Firebase Auth user with email/password.
   * Used by the signup component.
   */
  async signupWithEmailAndPassword(email: string, password: string) {
    const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
    const token = await userCredential.user.getIdToken();
    localStorage.setItem('loginData', JSON.stringify({ token }));
    return userCredential;
  }

  /**
   * Provisions a free-tier workspace for the currently authenticated user.
   * Calls the backend's /billing/free-signup endpoint which creates the
   * tenant, user record, and returns a custom login token.
   */
  createFreeWorkspace(data: { email: string; fullName: string }): Observable<{
    success: boolean;
    token: string;
    tenantId?: string;
    alreadyExists?: boolean;
    requireCheckout?: boolean;
    url?: string;
  }> {
    const payload = { ...data, locale: this.translate.currentLang || this.translate.getDefaultLang() || 'en' };
    return this.http.post<any>(`${this.BASE_URL_NO_AUTH}billing/free-signup`, payload, httpOptions);
  }

  createAccount(credentials): Observable<any> {
    return this.http.post(`${this.BASE_URL}/users/create`, credentials, {
      responseType: 'json',
    });
  }

  searchUsers(data): Observable<any> {
    if (data.searchInput.trim().length === 0) {
      data.searchInput = 'null';
    }
    return this.http.get(`${this.BASE_URL}/users/search-user/${data.pageSize}/${data.pageIndex}/${data.searchInput}`);
  }

  searchUserSimple(query): Observable<any> {
    return this.http.get(`${this.BASE_URL}/users/search?query=${query}`, httpOptions);
  }

  resetForgottenPassword(data): Observable<any> {
    return from(confirmPasswordReset(this.auth, data.token, data.newPassword));
  }

  verifyForgotMyPasswordToken(data): Observable<any> {
    return from(verifyPasswordResetCode(this.auth, data.token));
  }

  sendForgotMyPasswordEmail(data): Observable<any> {
    return this.http.post(`${this.BASE_URL}/users/forgotPassword`, data, httpOptions);
  }

  deleteUserAccount(data): Observable<any> {
    return this.http.post(`${this.BASE_URL}/users/deleteUserAccount`, data, httpOptions);
  }

  deleteAccountLocal(data): Observable<any> {
    return this.http.post(`${this.BASE_URL}/users/deleteUserLocal`, data, httpOptions);
  }

  enableUserAccount(data): Observable<any> {
    return this.http.post(`${this.BASE_URL}/users/renableAccount`, data, httpOptions);
  }

  setRole(data): Observable<any> {
    return this.http.post(`${this.BASE_URL}/users/setUserRole/`, data, httpOptions);
  }

  // ─── Tenant-Scoped User Management ──────────────────────────────────────

  getWorkspaceUsers(pageSize: number, pageIndex: number, searchTerm: string, sortField = '', sortDirection = '') {
    const params = new URLSearchParams({
      pageSize: String(pageSize),
      pageIndex: String(pageIndex),
      searchTerm: searchTerm || '',
    });
    if (sortField && sortDirection) {
      params.set('sortField', sortField);
      params.set('sortDirection', sortDirection);
    }
    return this.http.get<{ list: any[]; total: number }>(`${this.BASE_URL}/users/workspace-users?${params.toString()}`);
  }

  inviteUser(payload: { email?: string; emails?: string[]; role: string; groupId?: string | null }): Observable<{ success: boolean; msg: string; sentCount?: number; skippedCount?: number }> {
    return this.http.post<{ success: boolean; msg: string; sentCount?: number; skippedCount?: number }>(`${this.BASE_URL}/users/invite`, payload, {
      responseType: 'json',
    });
  }

  getJoinLink(): Observable<{ success: boolean; joinUrl: string; joinToken: string; enabled: boolean; groupId?: string | null }> {
    return this.http.get<{ success: boolean; joinUrl: string; joinToken: string; enabled: boolean; groupId?: string | null }>(`${this.BASE_URL}/users/join-link`);
  }

  toggleJoinLink(enabled: boolean, groupId?: string | null): Observable<{ success: boolean; enabled: boolean; groupId?: string | null }> {
    return this.http.post<{ success: boolean; enabled: boolean; groupId?: string | null }>(`${this.BASE_URL}/users/toggle-join-link`, { enabled, groupId });
  }

  getJoinLinks(): Observable<{ success: boolean; links: JoinLinkItem[] }> {
    return this.http.get<{ success: boolean; links: JoinLinkItem[] }>(`${this.BASE_URL}/users/join-links`);
  }

  createJoinLink(payload: { name: string; groupId?: string | null; allowedDomain?: string | null; role?: string }): Observable<{ success: boolean; link: JoinLinkItem }> {
    return this.http.post<{ success: boolean; link: JoinLinkItem }>(`${this.BASE_URL}/users/create-join-link`, payload);
  }

  updateJoinLink(id: string, payload: { name?: string; groupId?: string | null; allowedDomain?: string | null; role?: string; enabled?: boolean }): Observable<{ success: boolean; link: JoinLinkItem }> {
    return this.http.put<{ success: boolean; link: JoinLinkItem }>(`${this.BASE_URL}/users/update-join-link/${id}`, payload);
  }

  deleteJoinLink(id: string): Observable<{ success: boolean; id: string }> {
    return this.http.delete<{ success: boolean; id: string }>(`${this.BASE_URL}/users/delete-join-link/${id}`);
  }

  acceptJoinLink(joinToken: string): Observable<{ success: boolean; tenantId: string; msg: string }> {
    return this.http.post<{ success: boolean; tenantId: string; msg: string }>(`${this.BASE_URL}/users/accept-join-link`, { joinToken });
  }

  getDomainAutoJoin(): Observable<{ success: boolean; allowedDomains: string[]; enabled: boolean }> {
    return this.http.get<{ success: boolean; allowedDomains: string[]; enabled: boolean }>(`${this.BASE_URL}/users/domain-auto-join`);
  }

  updateDomainAutoJoin(payload: { allowedDomains: string[]; enabled: boolean }): Observable<{ success: boolean; allowedDomains: string[]; enabled: boolean }> {
    return this.http.post<{ success: boolean; allowedDomains: string[]; enabled: boolean }>(`${this.BASE_URL}/users/domain-auto-join`, payload);
  }

  checkDomainAutoJoin(): Observable<{ success: boolean; joinedTenants: string[] }> {
    return this.http.post<{ success: boolean; joinedTenants: string[] }>(`${this.BASE_URL}/users/check-domain-auto-join`, {});
  }

  getInviteInfo(token: string): Observable<{ success: boolean; payload: { email: string; role: string; workspaceName: string; inviterName: string; userExists: boolean } }> {
    return this.http.get<{ success: boolean; payload: { email: string; role: string; workspaceName: string; inviterName: string; userExists: boolean } }>(
      `${this.BASE_URL}/users/invite-info/${token}`
    );
  }

  getJoinLinkInfo(token: string): Observable<{ success: boolean; payload: { workspaceName: string; logoUrl: string | null; primaryColor: string | null; allowedDomain: string | null; creator: string | null; name: string | null } }> {
    return this.http.get<{ success: boolean; payload: { workspaceName: string; logoUrl: string | null; primaryColor: string | null; allowedDomain: string | null; creator: string | null; name: string | null } }>(
      `${this.BASE_URL}/users/join-link-info/${token}`
    );
  }

  acceptInvite(token: string): Observable<{ success: boolean; msg: string; tenantId?: string }> {
    return this.http.post<{ success: boolean; msg: string; tenantId?: string }>(
      `${this.BASE_URL}/users/accept-invite`,
      { token },
      { responseType: 'json' }
    ).pipe(
      tap(async (res) => {
        if (res.success && this.auth.currentUser) {
          try {
            await this.auth.currentUser.reload();
            const verified = this.auth.currentUser.emailVerified;
            this.isEmailVerified.set(verified);
            if (verified) {
              const tok = await this.auth.currentUser.getIdToken(true);
              const loginData = { token: tok };
              localStorage.setItem('loginData', JSON.stringify(loginData));
            }
          } catch (e) {
            console.error('Failed to reload firebase user or refresh token in acceptInvite', e);
          }
        }
      })
    );
  }

  getPendingInvitations(): Observable<{ list: PendingInvitation[]; total: number }> {
    return this.http.get<{ list: PendingInvitation[]; total: number }>(`${this.BASE_URL}/users/workspace-invitations`);
  }

  resendInvite(invitationId: string): Observable<{ success: boolean; msg: string }> {
    return this.http.post<{ success: boolean; msg: string }>(`${this.BASE_URL}/users/resend-invite`, { invitationId }, { responseType: 'json' });
  }

  cancelInvite(invitationId: string): Observable<{ success: boolean; msg: string }> {
    return this.http.delete<{ success: boolean; msg: string }>(`${this.BASE_URL}/users/cancel-invite/${invitationId}`, { responseType: 'json' });
  }

  setWorkspaceUserRole(userId: string, role: string): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`${this.BASE_URL}/users/set-role/${userId}`, { role }, { responseType: 'json' });
  }

  removeUserFromWorkspace(userId: string): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(`${this.BASE_URL}/users/remove/${userId}`, { responseType: 'json' });
  }

  updateAccount(details: any): Observable<void> {
    return this.http.post<void>(`${this.BASE_URL}/users/update/${details._id}`, details, {
      responseType: 'json',
    });
  }

  setPassword(credentials): Observable<any> {
    return this.http.post(`${this.BASE_URL}/users/resetPassword`, credentials, httpOptions);
  }

  updatePassword(credentials): Observable<any> {
    return this.http.post(`${this.BASE_URL}/users/update/password`, credentials, httpOptions);
  }

  register(credentials) {
    //  return this.http.post(`${this.BASE_URL}/register`, credentials)
    return this.http.post(`${this.BASE_URL}/register`, credentials);
  }
  loginWithToken(token, redirect = false) {
    return new Promise((resolve, reject) => {
      signInWithCustomToken(this.auth, token)
        .then(async (result) => {
          const loginData = {
            token: await result.user.getIdToken(),
          };
          localStorage.setItem('loginData', JSON.stringify(loginData));
          const user = result.user;
          this.getUserData({ id: user.uid })
            .then(async (res) => {
              if (!localStorage.getItem('sessionUUID')) {
                await this.saveUserSession(user.uid);
                this.startSessionSubscription(user.uid);
              }
              resolve(true);
              setTimeout(() => {
                if (redirect) {
                  location.reload();
                }
              }, 100);
            })
            .catch((err) => {
              this.logOut();
              reject();
            });
        })
        .catch((err) => {
          console.error('error with custom token', err);
          reject();
        });
    });
  }

  private getUserConfiguration(): void {
    this.userConfiguration$ = this.refreshConfig$.asObservable().pipe(
      switchMap(() => {
        if (!this.getTokenFromStorage() || this.auth.currentUser?.isAnonymous) {
          return of(this.getDefaultUserConfiguration());
        }
        return this.http.get<UserConfiguration>(`${this.BASE_URL_NO_AUTH}users/get-user-configuration/`, httpOptions);
      }),
      tap((configDoc) => {
        if (this.auth.currentUser && !this.auth.currentUser.isAnonymous) {
          console.log('Fetched new user configuration from API.');
          localStorage.setItem('configurations', JSON.stringify(configDoc));
        }
      }),
      catchError((error) => {
        console.error('Failed to fetch user configuration from API, trying localStorage.', error);
        const savedConfig = localStorage.getItem('configurations');
        if (savedConfig) {
          return of(JSON.parse(savedConfig));
        }
        console.warn('No configuration in localStorage, falling back to default.');
        return of(this.getDefaultUserConfiguration());
      }),
      map((element: any) => {
        if (element && element.__data) {
          const decryptedElement = JSON.parse(CryptoJS.AES.decrypt(element.__data, this.env.appName).toString(CryptoJS.enc.Utf8));
          decryptedElement.decrypted = true;
          return decryptedElement;
        }
        return element;
      }),
      // Replay ensures that the value is only fetched when the first subscriber calls the function
      // subsequent subscriber will receive the value that is already fetched.
      shareReplay(1)
    );
  }

  setUserConfiguration(data) {
    return this.http.post(`${this.BASE_URL_NO_AUTH}users/set-user-configuration/`, data, httpOptions).pipe(tap(() => this.refreshConfig$.next()));
  }

  getUserConfigurationDocument(): Promise<UserConfiguration> {
    return firstValueFrom(this.userConfiguration$);
  }

  getDefaultUserConfiguration(): UserConfiguration {
    return {
      language: this.translate.currentLang || 'fr',
      transcriptionAI: 'google',
      audioConfiguration: {
        en: {
          voiceType: 'en-US-Chirp3-HD-Orus',
          speed: this.defaultSliderValues.speed,
          pitch: this.defaultSliderValues.pitch,
        },
        fr: {
          voiceType: 'fr-FR-Chirp3-HD-Orus',
          speed: this.defaultSliderValues.speed,
          pitch: this.defaultSliderValues.pitch,
        },
      },
      menuItems: ['courses', 'blog', 'notes'],
      searchAI: 'gemini-3-flash-preview',
      chunkCreator: {
        generateTranslation: false,
        generateVoice: false,
        generateAiAfterTranscription: false,
        generateEmbeddings: false,
      },
      searchSettings: {
        textToSearch: '',
        compoundOptions: 'editions,tags',
        searchType: 'text',
        minScore: '0.89',
        includeOutOfContext: false,
      },
      transcriptionSettings: { model: 'chirp3', prompt: '', template: null },
      generativeAISettings: { template: null },
      paginator: {
        'models-picker': 5,
        articles: 12,
        knowledge: 12,
        notes: 9,
        users: 5,
        questions: 10,
        usage: 10,
        path: 10,
        models: 10,
        'path-contents': 10,
      },
      TTSAIModel: { en: 'google', fr: 'google' },
      popups: {
        microphoneAccess: true,
        cameraAccess: true,
      },
    };
  }

  async setUserConfigurationDocument(update) {
    return firstValueFrom(this.setUserConfiguration(update));
  }

  getDefaultSliderValues() {
    return this.defaultSliderValues;
  }
}
